import { createSlice } from "@reduxjs/toolkit"
import { withFirestoreHydration } from "../middleware/firestorePersistence"

// Initial state for user preferences
const initialState = {
  theme: "light", // 'light' | 'dark'
  language: "en",
  currency: "USD",
  dateFormat: "MM/DD/YYYY",
  timeFormat: "12h", // '12h' | '24h'
  notifications: {
    email: true,
    push: true,
    budgetAlerts: true,
    weeklyReports: true,
    monthlyReports: true,
  },
  budgetSettings: {
    defaultView: "monthly", // 'monthly' | 'yearly'
    chartType: "bar", // 'bar' | 'pie' | 'line'
    showCategories: true,
    autoCategorizationEnabled: true,
    budgetWarningThreshold: 0.8, // Warn when 80% of budget is used
  },
  dashboard: {
    layout: "default",
    widgets: [
      "monthlySpending",
      "budgetOverview",
      "recentTransactions",
      "categoryBreakdown",
    ],
    chartPreferences: {
      animations: true,
      colors: "default", // 'default' | 'colorful' | 'monochrome'
    },
  },
  _hydrated: false,
}

const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    // Theme and appearance
    setTheme: (state, action) => {
      state.theme = action.payload
    },

    setLanguage: (state, action) => {
      state.language = action.payload
    },

    setCurrency: (state, action) => {
      state.currency = action.payload
    },

    setDateFormat: (state, action) => {
      state.dateFormat = action.payload
    },

    setTimeFormat: (state, action) => {
      state.timeFormat = action.payload
    },

    // Notification preferences
    updateNotificationSettings: (state, action) => {
      state.notifications = {
        ...state.notifications,
        ...action.payload,
      }
    },

    toggleNotification: (state, action) => {
      const notificationType = action.payload
      state.notifications[notificationType] =
        !state.notifications[notificationType]
    },

    // Budget settings
    updateBudgetSettings: (state, action) => {
      state.budgetSettings = {
        ...state.budgetSettings,
        ...action.payload,
      }
    },

    setBudgetDefaultView: (state, action) => {
      state.budgetSettings.defaultView = action.payload
    },

    setBudgetChartType: (state, action) => {
      state.budgetSettings.chartType = action.payload
    },

    setBudgetWarningThreshold: (state, action) => {
      state.budgetSettings.budgetWarningThreshold = action.payload
    },

    toggleAutoCategorizationEnabled: (state) => {
      state.budgetSettings.autoCategorizationEnabled =
        !state.budgetSettings.autoCategorizationEnabled
    },

    toggleShowCategories: (state) => {
      state.budgetSettings.showCategories = !state.budgetSettings.showCategories
    },

    // Dashboard settings
    updateDashboardLayout: (state, action) => {
      state.dashboard.layout = action.payload
    },

    updateDashboardWidgets: (state, action) => {
      state.dashboard.widgets = action.payload
    },

    addDashboardWidget: (state, action) => {
      const widget = action.payload
      if (!state.dashboard.widgets.includes(widget)) {
        state.dashboard.widgets.push(widget)
      }
    },

    removeDashboardWidget: (state, action) => {
      const widget = action.payload
      state.dashboard.widgets = state.dashboard.widgets.filter(
        (w) => w !== widget
      )
    },

    updateChartPreferences: (state, action) => {
      state.dashboard.chartPreferences = {
        ...state.dashboard.chartPreferences,
        ...action.payload,
      }
    },

    toggleChartAnimations: (state) => {
      state.dashboard.chartPreferences.animations =
        !state.dashboard.chartPreferences.animations
    },

    setChartColorScheme: (state, action) => {
      state.dashboard.chartPreferences.colors = action.payload
    },

    // Reset to defaults
    resetToDefaults: (state) => {
      return { ...initialState, _hydrated: state._hydrated }
    },

    resetBudgetSettings: (state) => {
      state.budgetSettings = initialState.budgetSettings
    },

    resetNotificationSettings: (state) => {
      state.notifications = initialState.notifications
    },

    resetDashboardSettings: (state) => {
      state.dashboard = initialState.dashboard
    },

    // Firestore hydration (handled by middleware)
    hydrateFromFirestore: (state, action) => {
      return {
        ...state,
        ...action.payload,
        _hydrated: true,
      }
    },
  },
})

// Export actions
export const {
  setTheme,
  setLanguage,
  setCurrency,
  setDateFormat,
  setTimeFormat,
  updateNotificationSettings,
  toggleNotification,
  updateBudgetSettings,
  setBudgetDefaultView,
  setBudgetChartType,
  setBudgetWarningThreshold,
  toggleAutoCategorizationEnabled,
  toggleShowCategories,
  updateDashboardLayout,
  updateDashboardWidgets,
  addDashboardWidget,
  removeDashboardWidget,
  updateChartPreferences,
  toggleChartAnimations,
  setChartColorScheme,
  resetToDefaults,
  resetBudgetSettings,
  resetNotificationSettings,
  resetDashboardSettings,
  hydrateFromFirestore,
} = userPreferencesSlice.actions

// Selectors
export const selectTheme = (state) => state.userPreferences.theme
export const selectLanguage = (state) => state.userPreferences.language
export const selectCurrency = (state) => state.userPreferences.currency
export const selectDateFormat = (state) => state.userPreferences.dateFormat
export const selectTimeFormat = (state) => state.userPreferences.timeFormat
export const selectNotificationSettings = (state) =>
  state.userPreferences.notifications
export const selectBudgetSettings = (state) =>
  state.userPreferences.budgetSettings
export const selectDashboardSettings = (state) =>
  state.userPreferences.dashboard
export const selectChartPreferences = (state) =>
  state.userPreferences.dashboard.chartPreferences
export const selectUserPreferencesHydrated = (state) =>
  state.userPreferences._hydrated

// Composite selectors
export const selectBudgetDefaultView = (state) =>
  state.userPreferences.budgetSettings.defaultView
export const selectBudgetChartType = (state) =>
  state.userPreferences.budgetSettings.chartType
export const selectBudgetWarningThreshold = (state) =>
  state.userPreferences.budgetSettings.budgetWarningThreshold
export const selectAutoCategorizationEnabled = (state) =>
  state.userPreferences.budgetSettings.autoCategorizationEnabled
export const selectDashboardWidgets = (state) =>
  state.userPreferences.dashboard.widgets
export const selectChartAnimationsEnabled = (state) =>
  state.userPreferences.dashboard.chartPreferences.animations

// Enhanced reducer with Firestore hydration
export default withFirestoreHydration(userPreferencesSlice.reducer)
